import React, { useEffect, useState } from 'react';
import {
    EnvelopeIcon,
    BriefcaseIcon,
    BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { collection, getDocs, query, where } from 'firebase/firestore';
import DashboardWrapper from '../components/layout/DashboardWrapper';
import { useAuth } from '../contexts/AuthContext';
import { Employee } from '../types/hr';
import { db } from '../firebase/config';

const ProfilePage: React.FC = () => {
    const { currentUser, logout } = useAuth();
    const [employee, setEmployee] = useState<Employee | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!currentUser?.email) return;

        const load = async () => {
            try {
                const empSnap = await getDocs(query(collection(db, 'employees'), where('email', '==', currentUser.email)));
                if (!empSnap.empty) {
                    const d = empSnap.docs[0];
                    const data = d.data();
                    setEmployee({
                        id: d.id,
                        uid: typeof data.uid === 'string' ? data.uid : undefined,
                        employeeId: typeof data.employeeId === 'string' ? data.employeeId : '',
                        firstName: typeof data.firstName === 'string' ? data.firstName : '',
                        surname: typeof data.surname === 'string' ? data.surname : '',
                        email: typeof data.email === 'string' ? data.email : '',
                        department: typeof data.department === 'string' ? data.department : '',
                        jobTitle: typeof data.jobTitle === 'string' ? data.jobTitle : '',
                        status: 'active',
                    });
                }
            } catch {
                setError('Failed to load your profile. Please refresh and try again.');
            }
        };

        load();
    }, [currentUser]);

    const displayName = employee
        ? `${employee.firstName} ${employee.surname}`.trim()
        : currentUser?.displayName ?? currentUser?.email ?? '';

    const initials = displayName
        .split(' ')
        .filter(Boolean)
        .map((n) => n[0].toUpperCase())
        .slice(0, 2)
        .join('');

    return (
        <DashboardWrapper>
            <div className="w-full h-[calc(100vh-64px)] overflow-y-auto p-6 flex flex-col gap-6">

                {/* Page header */}
                <div className="flex items-center justify-between shrink-0">
                    <h1 className="text-2xl font-heading">My Profile</h1>
                    <button
                        onClick={() => logout().catch(() => setError('Sign out failed. Close this browser window to end the session, then try again.'))}
                        className="px-4 py-2 text-sm border rounded-lg bg-white hover:bg-gray-50 transition-colors"
                    >
                        Sign out
                    </button>
                </div>

                {error && (
                    <div className="p-3 text-sm bg-red-50 text-red-600 border border-red-200 rounded-lg shrink-0">
                        {error}
                    </div>
                )}

                {/* Profile card */}
                <div className="bg-white rounded-xl border p-6 flex items-center gap-6 shrink-0">
                        <div className="w-16 h-16 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold flex-shrink-0">
                            {initials || '?'}
                        </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-lg font-semibold text-gray-900 truncate">{displayName}</h2>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                            {currentUser?.email && (
                                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                                    <EnvelopeIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    {currentUser.email}
                                </span>
                            )}
                            {employee?.jobTitle && (
                                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                                    <BriefcaseIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    {employee.jobTitle}
                                </span>
                            )}
                            {employee?.department && (
                                <span className="flex items-center gap-1.5 text-sm text-gray-500">
                                    <BuildingOfficeIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                    {employee.department}
                                </span>
                            )}
                        </div>
                    </div>
                    {employee?.employeeId && (
                        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full shrink-0">
                            {employee.employeeId}
                        </span>
                    )}
                </div>

            </div>
        </DashboardWrapper>
    );
};

export default ProfilePage;
